import React, { useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import obwiCsvRaw from '../data/obwi.csv?raw';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import betsCsvRaw from '../data/obwi-bets.csv?raw';
import { getAvatarForUser } from '../lib/wrapped/avatarUtils';

type ObwiEntry = {
  miner: string;
  pwp: string;
  actual: string;
  delta: string;
  obwiIndex: number; // percentage as number (e.g., 1.26)
  heat: string;
  originalRank: number | null;
  heatWin: boolean;
  prize: string;
};

type BetEntry = {
  wagerer: string;
  bet: string;
  position: string;
  staked: string;
  date: string;
  payout: string;
};

function parseCsv(csvText: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const next = csvText[i + 1];
    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"';
        i++; // skip escaped quote
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        current.push(field);
        field = '';
      } else if (char === '\n' || char === '\r') {
        if (field.length > 0 || current.length > 0) {
          current.push(field);
          rows.push(current);
          current = [];
          field = '';
        } else {
          // skip stray newlines
          field = '';
        }
        // swallow \r\n as one
        if (char === '\r' && next === '\n') i++;
      } else {
        field += char;
      }
    }
  }
  if (field.length > 0 || current.length > 0) {
    current.push(field);
    rows.push(current);
  }
  // Remove any completely empty trailing rows
  return rows.filter(r => r.some(cell => cell.trim() !== ''));
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '');
}

function getAvatarObjectPosition(username: string): string {
  const key = normalizeName(username);
  if (key === 'pr_tek') return 'center 10%';
  if (key === 'mapster') return 'center 10%';
  if (key === 'coach_2') return 'center 20%';
  if (key === 'trimbawolf') return 'center 10%';
  if (key === 'barry') return 'center 2%';
  if (key === 'joni') return 'center 40%';
  if (key === 'rachoon') return 'center 35%';
  if (key === 'sam_spade') return 'center 20%';
  return 'center 25%';
}

const ObwiResults: React.FC = () => {
  const obwiRows = useMemo(() => parseCsv(obwiCsvRaw), []);
  const betsRows = useMemo(() => parseCsv(betsCsvRaw), []);

  const obwiData: ObwiEntry[] = useMemo(() => {
    const [header, ...rows] = obwiRows;
    // Expected header could be "User" or "OBWI Miner"
    return rows.map((r) => {
      const miner = (r[0] || '').trim();
      const pwp = (r[1] || '').trim();
      const actual = (r[2] || '').trim();
      const delta = (r[3] || '').trim();
      const indexStr = (r[4] || '').trim().replace('%', '');
      const obwiIndex = indexStr ? Number(indexStr) : Number.POSITIVE_INFINITY;
      const heat = (r[5] || '').trim();
      const originalRank = (r[6] && r[6].trim()) ? Number(r[6]) : null;
      const heatWin = ((r[7] || '').trim().toLowerCase() === 'y');
      const prize = (r[8] || '').trim();
      return { miner, pwp, actual, delta, obwiIndex, heat, originalRank, heatWin, prize };
    }).filter(e => e.miner !== '');
  }, [obwiRows]);

  const eRockKey = normalizeName('ERock');
  const rachoonKey = normalizeName('Rachoon');

  const withRanks = useMemo(() => {
    // Sort by OBWI Index ascending; include all, but mark ineligible ranks for ERock and Rachoon
    const sorted = [...obwiData].sort((a, b) => a.obwiIndex - b.obwiIndex);
    let rankCounter = 0;
    return sorted.map((e) => {
      const key = normalizeName(e.miner);
      const eligible = key !== eRockKey && key !== rachoonKey;
      const displayRank = eligible ? (++rankCounter) : null;
      return { ...e, displayRank } as any;
    });
  }, [obwiData]);

  const podium = useMemo(() => withRanks.filter((e: any) => e.displayRank).slice(0, 3), [withRanks]);

  const pwpByMiner = useMemo(() => {
    const map = new Map<string, string>();
    obwiData.forEach((e) => {
      map.set(normalizeName(e.miner), e.pwp);
    });
    return map;
  }, [obwiData]);

  const heatByMiner = useMemo(() => {
    const map = new Map<string, string>();
    obwiData.forEach((e) => {
      map.set(normalizeName(e.miner), e.heat);
    });
    return map;
  }, [obwiData]);

  const displayNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    obwiData.forEach((e) => {
      map.set(normalizeName(e.miner), e.miner);
    });
    return map;
  }, [obwiData]);

  const betsData: BetEntry[] = useMemo(() => {
    const [header, ...rows] = betsRows;
    return rows.map((r) => ({
      wagerer: (r[0] || '').trim(),
      bet: (r[1] || '').trim(),
      position: (r[2] || '').trim(),
      staked: (r[3] || '').trim(),
      date: (r[4] || '').trim(),
      payout: (r[5] || '').trim(),
    })).filter(e => e.wagerer !== '');
  }, [betsRows]);

  function extractAthleteFromPosition(position: string): string | null {
    if (!position) return null;
    // Remove known keywords and trim
    const cleaned = position
      .replace(/\b(Heat|Under|Over|PWP)\b/gi, '')
      .replace(/[,_-]/g, ' ')
      .trim();
    if (!cleaned) return null;
    // Take first word if multiple remain, unless it's like 'Sam Spade'
    const parts = cleaned.split(/\s+/);
    if (parts.length >= 2) {
      // Try to find best match by combining 1-2 parts
      const candidate2 = normalizeName(parts.slice(0, 2).join(' '));
      const candidate1 = normalizeName(parts[0]);
      if (pwpByMiner.has(candidate2)) return parts.slice(0, 2).join(' ');
      if (pwpByMiner.has(candidate1)) return parts[0];
      return parts[0];
    }
    return parts[0];
  }

  function getPwpForPosition(position: string): string | null {
    const athlete = extractAthleteFromPosition(position);
    if (!athlete) return null;
    const key = normalizeName(athlete);
    return pwpByMiner.get(key) || null;
  }

  // Sorting for Full Results
  type ResultsSortField = 'displayRank' | 'miner' | 'obwiIndex' | 'pwp' | 'actual' | 'heat';
  const [resultsSortField, setResultsSortField] = useState<ResultsSortField>('displayRank');
  const [resultsSortDir, setResultsSortDir] = useState<'asc' | 'desc'>('asc');
  const sortedResults = useMemo(() => {
    const arr = [...withRanks];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (resultsSortField) {
        case 'displayRank':
          cmp = ((a as any).displayRank ?? Number.POSITIVE_INFINITY) - ((b as any).displayRank ?? Number.POSITIVE_INFINITY);
          break;
        case 'miner':
          cmp = a.miner.localeCompare(b.miner);
          break;
        case 'obwiIndex':
          cmp = a.obwiIndex - b.obwiIndex;
          break;
        case 'pwp':
          cmp = (a.pwp || '').localeCompare(b.pwp || '');
          break;
        case 'actual':
          cmp = (a.actual || '').localeCompare(b.actual || '');
          break;
        case 'heat':
          cmp = (a.heat || '').localeCompare(b.heat || '');
          break;
      }
      return resultsSortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [withRanks, resultsSortField, resultsSortDir]);

  function toggleResultsSort(field: ResultsSortField) {
    if (resultsSortField === field) {
      setResultsSortDir(resultsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setResultsSortField(field);
      setResultsSortDir(field === 'displayRank' ? 'asc' : 'asc');
    }
  }

  // Sorting for Bets
  type BetsSortField = 'wagerer' | 'position' | 'staked' | 'payout';
  const [betsSortField, setBetsSortField] = useState<BetsSortField>('wagerer');
  const [betsSortDir, setBetsSortDir] = useState<'asc' | 'desc'>('asc');
  const sortedBets = useMemo(() => {
    const arr = betsData
      .filter(b => (b.bet || '').toUpperCase().includes('OBWI'))
      .map(b => {
        let positionDisplay = b.position;
        if (/\bHeat\b/i.test(b.position)) {
          const athlete = extractAthleteFromPosition(b.position);
          const key = athlete ? normalizeName(athlete) : '';
          const displayName = (key && displayNameByKey.get(key)) || (athlete || '');
          const heat = key ? heatByMiner.get(key) : undefined;
          positionDisplay = `${displayName} Heat (${heat ?? '-'})`;
        } else {
          const pwp = getPwpForPosition(b.position);
          positionDisplay = pwp ? `${b.position} (${pwp})` : b.position;
        }
        return { ...b, positionWithPwp: positionDisplay } as BetEntry & { positionWithPwp: string };
      });
    arr.sort((a, b) => {
      let cmp = 0;
      switch (betsSortField) {
        case 'wagerer':
          cmp = a.wagerer.localeCompare(b.wagerer);
          break;
        case 'position':
          cmp = (a as any).positionWithPwp.localeCompare((b as any).positionWithPwp);
          break;
        case 'staked':
          cmp = Number(a.staked || 0) - Number(b.staked || 0);
          break;
        case 'payout':
          cmp = Number(a.payout || 0) - Number(b.payout || 0);
          break;
      }
      return betsSortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [betsData, betsSortField, betsSortDir]);

  function toggleBetsSort(field: BetsSortField) {
    if (betsSortField === field) {
      setBetsSortDir(betsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setBetsSortField(field);
      setBetsSortDir('asc');
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">OBWI Results</h1>
          <p className="text-gray-600 mt-1">Sponsored by <a href="/old-balance" className="text-wingo-600 hover:text-wingo-700 font-semibold underline">Old Balance</a></p>
        </div>

        {/* Podium */}
        <section className="mb-10">
          <div className="grid grid-cols-3 gap-3 sm:gap-6 items-end text-center">
            {podium.map((p, idx) => {
              const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉';
              const userKey = normalizeName(p.miner);
              const borderClass = idx === 0
                ? 'border-[#E6C200]'
                : idx === 1
                ? 'border-gray-400'
                : 'border-orange-400';
              return (
                <div key={p.miner} className={`bg-white rounded-xl shadow-sm border-2 ${borderClass} p-3 overflow-hidden`}>
                  <div className="flex flex-col items-center">
                    {/* On mobile, show name + medal above avatar; on desktop, below avatar */}
                    <div className="order-1 sm:order-2 font-semibold text-gray-900 flex items-center gap-0.5 sm:gap-1 whitespace-nowrap text-sm sm:text-base pl-1 mb-1 sm:mt-2">
                      <span className="truncate max-w-[6.5rem] sm:max-w-none">{p.miner}{userKey === eRockKey ? '*' : ''}</span>
                      <span>{medal}</span>
                    </div>
                    <span className="order-2 sm:order-1 w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden inline-block">
                      <img src={getAvatarForUser(p.miner)} alt={p.miner} className="w-full h-full object-cover" style={{ objectPosition: getAvatarObjectPosition(p.miner) }} />
                    </span>
                    <div className="order-3 text-sm text-gray-800 font-semibold mt-1">OBWI Index: {isFinite(p.obwiIndex) ? `${p.obwiIndex.toFixed(2)}%` : '--'}</div>
                    <div className="order-3 text-[11px] text-gray-500 whitespace-nowrap">PWP: {p.pwp || '--'}</div>
                    <div className="order-3 text-[11px] text-gray-500 whitespace-nowrap">Actual: {p.actual || '--'}</div>
                    {(() => {
                      const prizeMap: Record<string, number> = {
                        'kirkland': 72,
                        'sam_spade': 36,
                        'trimbawolf': 12,
                      };
                      const amount = prizeMap[userKey];
                      return amount ? (
                        <div className="order-3 text-[11px] text-gray-700 font-semibold mt-0.5">Payout: <span className="text-[#E6C200] font-bold">W</span> {amount}</div>
                      ) : null;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Full Results */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">OBWI Results</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 font-mono text-[12px] sm:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleResultsSort('displayRank')}>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <span>Rank</span>
                      {resultsSortField==='displayRank' && (
                        <span>{resultsSortDir==='asc'?'↑':'↓'}</span>
                      )}
                    </span>
                  </th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleResultsSort('miner')}>Runner {resultsSortField==='miner' ? (resultsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleResultsSort('obwiIndex')}>OBWI Index {resultsSortField==='obwiIndex' ? (resultsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleResultsSort('pwp')}>PWP {resultsSortField==='pwp' ? (resultsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleResultsSort('actual')}>Actual {resultsSortField==='actual' ? (resultsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleResultsSort('heat')}>Heat {resultsSortField==='heat' ? (resultsSortDir==='asc'?'↑':'↓') : ''}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedResults.map((r) => (
                  <tr key={r.miner}>
                    <td className="px-1 sm:px-2 py-2">{(r as any).displayRank ?? '-'}</td>
                    <td className="px-1 sm:px-2 py-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full overflow-hidden inline-block">
                        <img src={getAvatarForUser(r.miner)} alt={r.miner} className="w-full h-full object-cover" style={{ objectPosition: getAvatarObjectPosition(r.miner) }} />
                      </span>
                      <span className="whitespace-nowrap">{r.miner}</span>
                    </td>
                    <td className="px-1 sm:px-2 py-2">{isFinite(r.obwiIndex) ? `${r.obwiIndex.toFixed(2)}%` : '--'}</td>
                    <td className="px-1 sm:px-2 py-2">{r.pwp || '--'}</td>
                    <td className="px-1 sm:px-2 py-2">{r.actual || '--'}</td>
                    <td className="px-1 sm:px-2 py-2">{r.heat || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">* ERock and Rachoon ineligible for podium.</p>
          <p className="text-xs text-gray-500 mt-1">
            PWP = PentaWingoPoint (DAISY-generated target time based on preliminary runner's PentaWingo). OBWI Index = (Actual − PWP) ÷ PWP. Closest to 0 wins.
          </p>
          <p className="text-xs text-gray-500 mt-1">Scar, Melathon, and Rachoon PWPs are based on adjusted walking distance/pace.</p>
        </section>

        {/* Bets with PWP */}
        <section>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">OBWI Bets</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 font-mono text-[13px] sm:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleBetsSort('wagerer')}>Wagerer {betsSortField==='wagerer' ? (betsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleBetsSort('position')}>Position {betsSortField==='position' ? (betsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleBetsSort('staked')}>Staked {betsSortField==='staked' ? (betsSortDir==='asc'?'↑':'↓') : ''}</th>
                  <th className="px-1 sm:px-2 py-2 text-left text-gray-600 cursor-pointer" onClick={() => toggleBetsSort('payout')}>Payout {betsSortField==='payout' ? (betsSortDir==='asc'?'↑':'↓') : ''}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedBets.map((b, i) => {
                  const payoutNum = Number(b.payout || 0);
                  const payoutClass = payoutNum > 0 ? 'text-green-600 font-bold' : '';
                  return (
                    <tr key={`${b.wagerer}-${i}`}>
                      <td className="px-1 sm:px-2 py-2 align-top">{b.wagerer}</td>
                      <td className="px-1 sm:px-2 py-2 align-top">{(b as any).positionWithPwp}</td>
                      <td className="px-1 sm:px-2 py-2 align-top">{b.staked}</td>
                      <td className={`px-1 sm:px-2 py-2 align-top ${payoutClass}`}>{b.payout || '0'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </div>
  );
};

export default ObwiResults;


